// =============================================================================
// GUARD — Este archivo se importa desde CLIENTE (apps/dashboard) Y SERVER
// (apps/worker-api). Es la fuente de verdad ÚNICA de statements y roles base
// del RBAC del Portal (ADR 0002).
//
// Reglas inviolables:
//   1. Solo imports isomórficos de `better-auth/plugins/.../access`. Esos tres
//      módulos exportan data (tuplas const) y un factory puro
//      (`createAccessControl`). No tienen side-effects, no abren conexiones,
//      no tocan filesystem.
//   2. PROHIBIDO importar: `drizzle-orm`, `@better-auth/drizzle-adapter`, `pg`,
//      la schema (`../persistence/schema`), `fs`, `node:*`, ningún adapter
//      server-only. Cualquiera de esos rompe:
//         - El bundle del Worker (cold-start).
//         - El build del dashboard (Vite no resuelve módulos node:*).
//         - El invariante de aislamiento de planos (este archivo no debe
//           leer/escribir DB; eso lo hace `platform-roles.ts` del worker).
//   3. NUNCA reordenar acciones dentro de los statements ni renombrar roles
//      sin auditoría de seguridad: un reorder silencioso es un cambio
//      semántico oculto y la categoría más difícil de detectar en review
//      (ADR 0002, red-flag #4).
//   4. Si hace falta agregar un statement nuevo, hacerlo SOLO acá. Worker y
//      dashboard lo reciben en el mismo commit.
// =============================================================================

import { createAccessControl } from "better-auth/plugins/access";
import { defaultStatements as adminDefaultStatements } from "better-auth/plugins/admin/access";
import { defaultStatements as orgDefaultStatements } from "better-auth/plugins/organization/access";

/**
 * Catálogos de permisos (statements) — SEPARADOS por plano.
 *
 * Diseño: docs/RBAC_DESIGN.md. Dos planos aislados por construcción:
 * un permiso de plataforma es físicamente inalcanzable desde un rol de
 * organización porque cada plugin recibe solo su propio catálogo.
 */

// ---------------------------------------------------------------------------
// Plano PLATAFORMA — empleados de Tagliapietra. Rol en platformMembership,
// resuelto por platform-roles.ts (NO usa el plugin admin de Better Auth).
// ---------------------------------------------------------------------------

export const platformStatement = {
  // `user` y `session` se heredan del catálogo del admin plugin solo como
  // vocabulario de acciones (ban, impersonate, set-role…); el enforcement es
  // nuestro (platform-roles.ts), no del plugin. Mantenerlos da nombres
  // estables para cuando se construyan esos endpoints custom.
  ...adminDefaultStatements,
  // `org:suspend` — habilita los endpoints POST /api/platform/tenants/:slug/suspend
  // y /unsuspend (F3 Susp-2). Lo poseen superAdmin y studio-admin; studio-staff
  // queda fuera (acción operacional sensible: bloquea el acceso de todos los
  // miembros del tenant). El guardrail anti-escalada del Dynamic AC custom
  // (platform-roles.ts → canActorGrant/assertNoEscalation) ya cubre este
  // permiso por shape genérico — un rol dinámico sin `org:suspend` no puede
  // crearse/asignarse otorgándolo.
  //
  // `org:create` — ADR 0013 §Decisión: **prerrogativa exclusiva de los roles
  // base globales `superAdmin`/`studio-admin`**. Los guards de crear/editar
  // `platformRole` (platform-roles.ts + `assertPlatformDynamicRoleAllowed`
  // del paquete shared) rechazan esta verba en cualquier rol dinámico. No
  // vive en `platformOrgStatement` (una organización nueva no tiene target
  // que scopear — es intrínsecamente global).
  org: ["create", "update", "delete", "access", "suspend"],
  billing: ["read", "update"],
  platformRole: ["create", "update", "delete"],
  // ADR 0008 §5 Eje B — verba para gestionar filas en `platformOrgAssignment`.
  // La poseen superAdmin y studio-admin. Un studio-staff (o cualquier rol
  // scoped) NO la posee — no puede gestionar quién más entra a "sus" clientes.
  platformOrgAssignment: ["assign"],
  audit: ["read"],
  // `platform:configure` — acción de configuración global de la plataforma
  // (Fase 2: settings de consultora, integraciones, ramas de Sheets, etc.).
  // SOLO el rol `superAdmin` la posee. studio-admin / studio-staff NO la
  // tienen — asegura que el guardrail anti-escalada (canActorGrant) impida
  // que cualquier rol dinámico no-superAdmin la otorgue o ejerza.
  platform: ["configure"],
  // `dashboard:manage` — Dashboard Builder (ADR 0006 §Permisos).
  //
  // Permiso de PLATAFORMA para crear/editar/publicar/archivar dashboards,
  // tabs, widgets, filtros y datasources de un tenant destino. Lo poseen
  // superAdmin y studio-admin. studio-staff queda fuera por diseño: editar
  // SQL libre y publicar un dashboard son acciones sensibles que requieren
  // el rol con `org:["update","suspend"]` (admin), no el rol de operación
  // diaria (staff). El guardrail anti-escalada genérico (assertNoEscalation
  // en platform-roles.ts) ya cubre que un rol dinámico no pueda otorgárselo
  // sin poseerlo.
  //
  // F1 no expone `dashboard:read` separado: la lectura del builder está
  // gated por `manage` (mismo grupo de roles). Si en F3+ aparece un perfil
  // "studio-analyst" que solo previsualiza, ahí se añade `read` y los roles
  // se reorganizan. El ADR menciona `dashboard:read` y `datasource:[manage,
  // execute]`; en F1 colapsamos esos cuatro en uno solo (`dashboard:manage`)
  // para mantener el catálogo chico — el aislamiento real lo da el sandbox
  // (rol PG etp_bi_ro + RLS), no la granularidad del permiso de app.
  //
  // El consumo TENANT del dashboard publicado (ver datos en runtime) NO
  // gates por un permiso nuevo en F1: usa la membresía de la org dueña del
  // dashboard. Cualquier miembro de la org ve sus dashboards publicados —
  // suficiente para validar el flujo end-to-end. En F3+ se puede agregar un
  // permiso fino al `orgStatement` si hace falta granularidad por widget.
  dashboard: ["manage"],
} as const;

export const platformAc = createAccessControl(platformStatement);

/** Todo el catálogo de plataforma. Único rol que gestiona platformRole y borra orgs. */
export const superAdmin = platformAc.newRole({
  user: [...platformStatement.user],
  session: [...platformStatement.session],
  org: [...platformStatement.org],
  billing: [...platformStatement.billing],
  platformRole: [...platformStatement.platformRole],
  // ADR 0008 §5 Eje B — gestiona asignaciones por-cliente.
  platformOrgAssignment: [...platformStatement.platformOrgAssignment],
  audit: [...platformStatement.audit],
  // Configuración global: exclusivo de superAdmin. NO sumar a studio-admin
  // ni studio-staff (intencional — ver platformStatement arriba).
  platform: [...platformStatement.platform],
  // Dashboard Builder: superAdmin tiene todo lo del statement (manage).
  dashboard: [...platformStatement.dashboard],
});

/**
 * Operación completa de la consultora: gestiona el equipo (asigna roles
 * studio-admin/studio-staff/dinámicos, invita nuevos por email — ADR 0012),
 * pero NO puede asignar `superAdmin` (invariante ADR 0011 — enforced por
 * assignPlatformRole capa A y por trigger DB) ni borrar orgs.
 *
 * `user:["set-role"]` HABILITA:
 *  - `PUT /api/platform/memberships/:userId` (asignar rol a user existente).
 *  - `POST /api/platform/invitations` (invitar por email — ADR 0012).
 * Sigue limitado por `assertNoEscalation` (no puede asignar un rol con
 * permisos superset de los propios) y por el rechazo explícito de
 * `superAdmin` en ambos endpoints.
 */
export const studioAdmin = platformAc.newRole({
  user: ["create", "list", "ban", "impersonate", "set-password", "get", "update", "set-role"],
  session: ["list", "revoke", "delete"],
  // `suspend` NO está en studio-staff: suspender un tenant bloquea el acceso
  // de todos sus miembros — acción operacional sensible reservada a admin+.
  org: ["create", "update", "access", "suspend"],
  billing: ["read", "update"],
  // ADR 0008 §5 Eje B — studio-admin gestiona asignaciones por-cliente
  // (asignar staff a cada uno de sus clientes con el rol por-cliente que
  // corresponda).
  platformOrgAssignment: ["assign"],
  // Dashboard Builder: studio-admin construye dashboards para clientes.
  // (decisión CTO: studio-staff queda fuera — ver comentario en
  // platformStatement.dashboard).
  dashboard: ["manage"],
});

/**
 * Rol de operación diaria — SIEMPRE por-asignación (ADR 0008 §4 + ADR 0013).
 *
 * REDUCCIÓN DELIBERADA (ADR 0008 §4): antes de esta ADR, studio-staff tenía
 * `user:[list,get]`, `session:[list]`, `billing:[read]` como permisos GLOBALES.
 * Eran boquetes cross-org (staff podía listar usuarios/facturación de tenants
 * que no le fueron asignados). Post-ADR, todo eso se resuelve por el rol
 * por-cliente atado a `platformOrgAssignment` (catálogo `platformOrgStatement`
 * abajo). El único permiso GLOBAL que queda es `org:["access"]` — que solo
 * habilita el "listar mi cartera de clientes" (`GET /api/platform/tenants`
 * filtra por asignación); no habilita ver datos del tenant.
 *
 * Consecuencia dura (fail-closed absoluto, ADR 0013 §Decisión):
 *   - Un studio-staff con CERO filas en `platformOrgAssignment` no ve ni lista
 *     miembros, ni facturación, ni dashboards de NINGUNA org.
 *   - No hay ninguna vía — ni sumar `org:["create"]` a un rol dinámico
 *     (rechazado por `assertPlatformDynamicRoleAllowed`), ni ningún otro
 *     truco — que convierta a un rol acotado en global. Los únicos globales
 *     son `superAdmin` y `studio-admin` por NOMBRE del rol base.
 *
 * MIGRACIÓN DE DATOS (responsabilidad del deploy): antes de aplicar este
 * cambio en stage/prod, hay que crear filas en `platformOrgAssignment` para
 * cualquier `studio-staff` existente (o bajarlo a rol distinto). Verificado
 * al escribir esta ADR (2026-07): NO hay staff sembrado hoy — el impacto es
 * solo semántico sobre el rol base, sin datos que migrar.
 */
export const studioStaff = platformAc.newRole({
  org: ["access"],
});

/**
 * Roles base del plano plataforma. Los dinámicos viven en la tabla
 * platformRole. No hay rol "user"/vacío: la ausencia de fila en
 * platformMembership ya significa "sin poder de plataforma".
 */
export const platformRoles = {
  superAdmin,
  "studio-admin": studioAdmin,
  "studio-staff": studioStaff,
};

// ---------------------------------------------------------------------------
// Plano PLATAFORMA-POR-CLIENTE (ADR 0008 §3) — features de plataforma-sobre-tenant
// que un studio-staff (o cualquier rol scoped) ejerce SOBRE una organización
// concreta, resuelto por su fila en `platformOrgAssignment`.
//
// SEPARADO de `platformStatement` (rol GLOBAL de la persona en el estudio) y
// de `orgStatement` (rol TENANT-interno del miembro del cliente). Los tres
// catálogos NO se cruzan por construcción: un permiso de este catálogo NO
// puede sintetizarse desde un rol global (se resuelve por la asignación
// por-cliente), y viceversa. Confusión de planos entre statements es el
// riesgo #1 de ADR 0008 §Riesgos — mantener los tres arrays SEPARADOS y con
// comentarios de guardia es la mitigación.
//
// VERBOS ABIERTOS (ADR 0008 §3): el catálogo NO cierra a `{read, update,
// delete}`. Features reales tienen verbos propios (`account-statement:settle`
// es el ejemplo canónico). El validador `assertValidPermissions` chequea
// que cada `(feature, verbo)` esté en este catálogo — el catálogo se amplía
// aditivamente en PRs con review de auth-dev + security-auditor cuando
// aparecen features nuevas.
// ---------------------------------------------------------------------------

export const platformOrgStatement = {
  // Ver/gestionar la ficha del cliente desde la consola de plataforma. NO es
  // `orgStatement.organization` del plano tenant: esto es "el estudio opera
  // SOBRE este cliente", no "el cliente se opera a sí mismo".
  tenant: ["read", "update"],

  // Miembros del cliente vistos desde plataforma (listar, invitar). Reemplaza
  // el gate anterior `org:["update"]` para POST /tenants/:slug/invitations
  // (ADR 0008 §6): invitar cae acá, editar la ficha cae en `tenant:["update"]`.
  member: ["read", "invite"],

  // Dashboard Builder por-cliente (ADR 0006). El gate del sub-router
  // `/api/platform/dashboards/*` pasa a validar por-organización: `read` para
  // GETs, `manage` para mutaciones (ADR 0008 §7).
  dashboard: ["read", "manage"],

  // Facturación desde el punto de vista de plataforma. `read` para consultar,
  // `update` para modificar (planes, límites, cargos manuales).
  billing: ["read", "update"],

  // Ejemplo ilustrativo del modelo (feature NO diseñada acá — ver ADR 0008
  // §"Qué NO se construye"). El verbo `settle` (liquidar) demuestra por qué
  // el catálogo NO se cierra a {read, update, delete}: features reales tienen
  // verbos propios. `gestor` puede liquidar, `revisor` solo lee.
  "account-statement": ["read", "write", "settle"],

  // Auditoría por-cliente (ADR 0008 §3 — decisión producto 2026-08-14).
  //
  // Ampliación aditiva del catálogo por primera vez desde su nacimiento (era
  // el ítem P3 "Ampliación aditiva del catálogo platformOrgStatement" en
  // TODO.md). Habilita a un actor scoped (studio-staff via rol dinámico
  // por-cliente) a leer el `securityAuditLog` filtrado por su org asignada.
  //
  // DELEGACIÓN EN CADENA (invariante nuevo — 2026-08-14). El verbo `audit:read`
  // NO se hereda por posición jerárquica; se DELEGA explícitamente:
  //   1. `superAdmin` lo posee en el catálogo global (`platformStatement`,
  //      arriba) desde siempre.
  //   2. `studio-admin` lo recibe de `superAdmin` creando un rol dinámico de
  //      plataforma (`platformRole`) que lo incluya y asignándoselo — así
  //      queda registrado en `platformMembership.role` como rol dinámico
  //      global. El guardrail anti-escalada (`canActorGrant` →
  //      `canPlatformDo`) exige que superAdmin lo posea para poder otorgarlo.
  //   3. `studio-staff` lo recibe de `studio-admin` creando un rol por-cliente
  //      dinámico (`platformOrgRole`) que lo incluya y asignándoselo sobre
  //      orgs concretas — así el permiso queda acotado por
  //      `platformOrgAssignment`. El mismo guardrail
  //      (`assertNoOrgEscalation` → `assertNoEscalation`) exige que el actor
  //      posea el verbo GLOBAL para poder otorgarlo como verbo por-cliente.
  //
  // Consecuencia deliberada: NINGÚN rol base incluye `audit:["read"]` — ni
  // `studio-admin` (global), ni `studio-staff` (global), ni `revisor` /
  // `gestor` / `empleado` (por-cliente). Un `grep '"read"' permissions.ts`
  // debería mostrar `audit` en `superAdmin` y en el statement, pero NO en
  // ninguno de los `.newRole({ ... })` de la sección de por-cliente. Si algún
  // día se agrega a un rol base, se está violando el invariante — abrir ADR
  // que lo justifique antes.
  //
  // NOTA sobre "global-por-nombre" vs "verbo por-cliente" (`GET /api/platform/
  // audit?orgId=`): el gate NO puede resolverse con `hasPlatformPermission
  // ({plane:"org", perms:{audit:["read"]}, orgId})`. Su semántica actual
  // (ADR 0008 §5 Eje A) es "los globales cubren TODO el `platformOrgStatement`
  // por definición" y hace short-circuit devolviendo `true` para
  // `scope.mode === "global"`. Ese invariante sostiene todo el resto de
  // features del catálogo (dashboard, tenant, member, billing,
  // account-statement) y NO se toca acá: para `audit:read`, en cambio, el
  // gate compone dos chequeos en OR — global sobre `platformStatement` +
  // por-cliente sobre `platformOrgStatement` — con la lógica detallada
  // inline en `platform-router.ts` (handler `GET /audit`).
  audit: ["read"],
} as const;

export const platformOrgAc = createAccessControl(platformOrgStatement);

/**
 * Roles semilla del plano plataforma-por-cliente (ADR 0008 §3).
 *
 * Estos son roles BASE en código; el studio-admin puede crear más roles
 * dinámicos desde la consola (viven en `platformOrgRole`). La resolución
 * sigue el mismo patrón que `platformRoles`: base primero, dinámico después.
 *
 * Todos son intrínsecamente SCOPED (no habilitan nada globalmente — la
 * pertenencia a "esta org concreta" viene de la fila en `platformOrgAssignment`).
 */

/** Solo lectura de todas las features del cliente asignado. */
export const revisor = platformOrgAc.newRole({
  tenant: ["read"],
  member: ["read"],
  dashboard: ["read"],
  billing: ["read"],
  "account-statement": ["read"],
});

/** Operación completa sobre el cliente asignado. Máximo poder scoped. */
export const gestor = platformOrgAc.newRole({
  tenant: ["read", "update"],
  member: ["read", "invite"],
  dashboard: ["read", "manage"],
  billing: ["read", "update"],
  "account-statement": ["read", "write", "settle"],
});

/**
 * Perfil intermedio: lee todo el cliente y puede sumar managers (member:invite)
 * pero sin poder editar la ficha ni gestionar facturación/dashboards/cuentas.
 */
export const empleado = platformOrgAc.newRole({
  tenant: ["read"],
  member: ["read", "invite"],
  dashboard: ["read"],
  billing: ["read"],
  "account-statement": ["read"],
});

/**
 * Roles base del plano plataforma-por-cliente. Los dinámicos viven en
 * `platformOrgRole`. La resolución (base primero, dinámico después) vive en
 * el worker (`resolvePlatformOrgPermissions` en platform-roles.ts).
 */
export const platformOrgRoles = {
  revisor,
  gestor,
  empleado,
};

// ---------------------------------------------------------------------------
// Plano TENANT — organizaciones cliente. Plugin `organization`, rol en member.role
// ---------------------------------------------------------------------------

export const orgStatement = {
  // Statements nativos del organization plugin: organization, member,
  // invitation, team y `ac`. El recurso `ac` es el guardrail nativo de
  // roles dinámicos (el `orgRole` del diseño): solo roles con permisos
  // sobre `ac` pueden crear/editar roles, y nunca con permisos que el
  // creador no posee.
  ...orgDefaultStatements,
  sheet: ["read"],
  billing: ["read"],
} as const;

export const orgAc = createAccessControl(orgStatement);

export const owner = orgAc.newRole({
  organization: ["update", "delete"],
  member: ["create", "update", "delete"],
  invitation: ["create", "cancel"],
  ac: ["create", "read", "update", "delete"],
  sheet: ["read"],
  billing: ["read"],
});

export const orgAdmin = orgAc.newRole({
  organization: ["update"],
  member: ["create", "update", "delete"],
  invitation: ["create", "cancel"],
  ac: ["create", "read", "update", "delete"],
  sheet: ["read"],
  billing: ["read"],
});

export const orgMember = orgAc.newRole({
  sheet: ["read"],
});

/** Roles base del plano tenant. Los dinámicos viven en organizationRole (Dynamic AC). */
export const orgRoles = {
  owner,
  admin: orgAdmin,
  member: orgMember,
};
